import React, { useCallback, useEffect, useState } from "react";
import { styled, Switch, Box, Tooltip } from "@mui/material";
import { InactiveDialog } from "..";

interface CustomSwitchProps {
  isActive: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onConfirmInactive?: () => void;
  isConfirmationOnActive: boolean;
  isConfirmationOnInActive: boolean;
  confirmMessageActive?: string;
  confirmMessageInactive?: string;
  confirmTitleActive?: string;
  confirmTitleInactive?: string;
  IsReadOnly?:boolean;
  ReadonlyTitle?:string;
  Title?:string;
}

export const CustomSwitchStyled = styled(Switch)({
  width: 38,
  height: 24,
  padding: 0,
  display: "flex",
  "&:active": {
    "& .MuiSwitch-thumb": {
      width: 19,
    },
    "& .MuiSwitch-switchBase.Mui-checked": {
      transform: "translateX(10px)",
    },
  },
  "& .MuiSwitch-switchBase": {
    padding: 3,
    "&.Mui-checked": {
      transform: "translateX(14px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        opacity: 1,
        backgroundColor: "#414ED5",
      },
    },
  },
  "& .MuiSwitch-thumb": {
    boxShadow: "0 2px 4px 0 rgb(0 35 11 / 20%)",
    width: 19,
    height: 19,
    borderRadius: 10,
    transition: "all 200ms",
  },
  "& .MuiSwitch-track": {
    borderRadius: 12,
    opacity: 1,
    backgroundColor: "#d8d8d8",
    boxSizing: "border-box",
  },
});

const CustomSwitch: React.FC<CustomSwitchProps> = ({
  isActive,
  onChange,
  onConfirmInactive,
  confirmMessageActive,
  confirmMessageInactive,
  confirmTitleActive,
  confirmTitleInactive,
  isConfirmationOnActive,
  isConfirmationOnInActive,
  IsReadOnly,
  ReadonlyTitle,
  Title
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [checked, setChecked] = useState(isActive);
  const [pendingChecked, setPendingChecked] = useState<boolean | null>(null);

  useEffect(() => {
    setChecked(isActive);
  }, [isActive]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newChecked = event.target.checked;

    const isActivating = newChecked === true;
    const shouldShowDialog =
      (isActivating && isConfirmationOnActive) ||
      (!isActivating && isConfirmationOnInActive);

    if (shouldShowDialog) {
      setPendingChecked(newChecked);
      setDialogOpen(true);
    } else {
      setChecked(newChecked);
      if (onChange) {
        onChange(event);
      }
      if (!newChecked && onConfirmInactive) {
        onConfirmInactive();
      }
    }
  };

  const onClickDialogClose = useCallback(() => {
    setDialogOpen(false);
    setPendingChecked(null);
  }, []);

  const handleConfirm = useCallback(() => {
    if (pendingChecked !== null) {
      setChecked(pendingChecked);
      if (onChange) {
        onChange({
          target: { checked: pendingChecked },
        } as React.ChangeEvent<HTMLInputElement>);
      }
      if (!pendingChecked && onConfirmInactive) {
        onConfirmInactive();
      }
    }
    setDialogOpen(false);
    setPendingChecked(null);
  }, [onChange, onConfirmInactive, pendingChecked]);
  const currentMessage =
    pendingChecked === true
      ? confirmMessageActive || ""
      : confirmMessageInactive || "";
  const currentTitle =
    pendingChecked === true
      ? confirmTitleActive || ""
      : confirmTitleInactive || "";
return (
  <Box display="flex" alignSelf="center" alignItems="center">
    <Tooltip title={IsReadOnly ? ReadonlyTitle || '' : Title || ''}>
      <span>
        <CustomSwitchStyled
          checked={checked}
          onChange={handleChange}
          disabled={IsReadOnly}
        />
      </span>
    </Tooltip>
    <InactiveDialog
      open={dialogOpen}
      onClose={onClickDialogClose}
      onConfirm={handleConfirm}
      message={currentMessage}
      title={currentTitle}
    />
  </Box>
);
};

export default CustomSwitch;
